import { ProcessPictureElement } from "@fidelix/fx-margaret";
import { DateTime } from "luxon";
import { GraphicHistoryItem, GraphicValue } from "../graphics.queries";
import { convertValueFromOldRangeToNewRange } from "../../chart/chart.utils";
import { getMinAndMax } from "../../utils/math.utils";
import { Point } from "../../point/point.model";

const MAX_ITERATIONS = 1000;

// Some points only update once per hour. A point without a sample within this
// window before the playhead is shown as "No data".
export const seekLookbackSeconds = 60 * 60;

// Cache the current graphics element, the history dataset and the timestamps
// of the points in the graphics. This is done because element.setValue is an
// expensive operation and we want to _only_ update points in the graphic that
// have an updated timestamp.
let cachedElement: ProcessPictureElement | undefined = undefined;
let cachedHistoryData: GraphicHistoryItem | undefined = undefined;
let cachedTimestamps = new Map<string, string>();
let cachedNoDataKeys: string | undefined = undefined;

export function resetGraphicsValuesCache() {
  cachedElement = undefined;
  cachedHistoryData = undefined;
  cachedTimestamps = new Map();
  cachedNoDataKeys = undefined;
}

type UpdateValues = {
  timestampSeconds: DateTime;
  isFetching: boolean;
  historyData: GraphicHistoryItem;
  element: ProcessPictureElement;
  isPlaying: boolean;
  // Margaret keys (`deviceId.pointName`) of all points in the graphic
  pointKeys: string[];
};

export function updateGraphicsValues({
  element,
  historyData,
  isFetching,
  timestampSeconds,
  pointKeys,
}: UpdateValues) {
  if (!historyData || !element || !timestampSeconds || isFetching) {
    return;
  }

  // Reset the cache when the graphic element changes or a different history
  // dataset is supplied (e.g. a new chunk fetched after scrubbing), but keep
  // it across repeated calls with the same dataset so unchanged values aren't
  // resent while paused/scrubbing.
  if (cachedElement !== element || cachedHistoryData !== historyData) {
    resetGraphicsValuesCache();
    cachedElement = element;
    cachedHistoryData = historyData;
  }

  const { values, noDataKeys } = getValuesAtTimestamp(
    historyData,
    timestampSeconds,
    pointKeys,
  );

  // Must run before setValue: clearing a no data point removes it from the store
  const noDataKeysId = noDataKeys.join(",");
  if (noDataKeysId !== cachedNoDataKeys) {
    cachedNoDataKeys = noDataKeysId;
    element.setNoData(noDataKeys);
  }

  if (values.length === 0) {
    return;
  }

  element.setValue(
    "values",
    values.map((v) => ({
      ...v,
      timestamp: v.timestampJS.toJSDate().toISOString(),
      state: v.label ?? null,
      extension: v.extension ?? null,
    })),
  );
}

// Use binary search to find, per point, the last sample at or before `timestamp`.
// Points without a sample within the lookback window are returned as no data.
function getValuesAtTimestamp(
  historyData: GraphicHistoryItem,
  timestamp: DateTime,
  pointKeys: string[],
) {
  const values: GraphicValue[] = [];
  const noDataKeys: string[] = [];
  const keysWithHistory = new Set<string>();
  const minTimestamp = timestamp.minus({ seconds: seekLookbackSeconds });

  Object.values(historyData).forEach((d) => {
    if (d.length === 0) {
      return;
    }

    const key = `${d[0].deviceId}.${d[0].pointName}`;
    keysWithHistory.add(key);

    let start = 0;
    let end = d.length - 1;
    let i = 0;
    while (start < end && i < MAX_ITERATIONS) {
      i++;
      const mid = Math.ceil((start + end) / 2);
      if (d[mid].timestampJS <= timestamp) {
        start = mid;
      } else {
        end = mid - 1;
      }
    }

    const sample = d[start];
    if (sample.timestampJS > timestamp || sample.timestampJS < minTimestamp) {
      noDataKeys.push(key);
      // Ensure the value is resent once the point has data again
      cachedTimestamps.delete(key);
      return;
    }

    if (cachedTimestamps.get(key) !== sample.timestamp) {
      values.push(sample);
      cachedTimestamps.set(key, sample.timestamp);
    }
  });

  pointKeys.forEach((key) => {
    if (!keysWithHistory.has(key)) {
      noDataKeys.push(key);
    }
  });

  return { values, noDataKeys };
}

// The threshold when we also want to show seconds in the timestamp text
//
const threshold = 2 * 60 * 60;
// Convert x-axis pixel location to a timestamp to show on the timeline
export function convertPositionToTimeText(
  x: number,
  posRange: [number, number],
  timeRangeInseconds: [number, number],
  style?: Intl.DateTimeFormatOptions,
) {
  const seconds = Math.ceil(
    convertValueFromOldRangeToNewRange(x, posRange, timeRangeInseconds),
  );

  const localeStyle = style
    ? style
    : timeRangeInseconds[1] - timeRangeInseconds[0] < threshold
      ? DateTime.TIME_WITH_SECONDS
      : DateTime.TIME_SIMPLE;
  return DateTime.fromSeconds(seconds).toLocaleString(localeStyle);
}

export function areSameDate(date1: DateTime, date2: DateTime): boolean {
  return (
    date1.day === date2.day &&
    date1.month === date2.month &&
    date1.year === date2.year
  );
}

/**
 *
 * Removes milliseconds from a date - this is useful for consitency in backend queries
 * Takes in a DateTime object or a string in ISO format
 */
export function removeMillisFromDate<T extends string | DateTime>(date: T): T {
  if (date instanceof DateTime) {
    return date.startOf("second") as T;
  }
  return DateTime.fromISO(date)
    .startOf("second")
    .toISO({ suppressMilliseconds: true }) as T;
}

/**
 *
 * Normalize values to between 0 and 1
 */
export function normalizeGraphicValues(values: GraphicValue[]) {
  const numValues = values.filter(
    (v) => v.value != undefined && !isNaN(v.value),
  );

  if (numValues.length === 0) {
    return values;
  }

  const { min, max } = getMinAndMax(numValues.map((v) => v.value as number));

  // In some cases the min and max values are the same, in this case
  // return 0.5 as the value so the straight line right in the middle of the graph
  const drawInMiddle = min === max;

  const normalized = values.map((v) =>
    v.value != undefined && !isNaN(v.value)
      ? {
          ...v,
          value: drawInMiddle
            ? 0.5
            : convertValueFromOldRangeToNewRange(v.value, [min, max], [0, 1]),
        }
      : v,
  );

  return normalized;
}

export function convertTimestampSecondsToTimelinePixels(
  positionInSeconds: number,
  domainInSeconds: [number, number],
  timeLineWidthInPixels: number,
): number {
  if (positionInSeconds <= domainInSeconds[0]) {
    return 0;
  }
  if (positionInSeconds >= domainInSeconds[1]) {
    return timeLineWidthInPixels;
  }
  return convertValueFromOldRangeToNewRange(
    positionInSeconds,
    domainInSeconds,
    [0, timeLineWidthInPixels],
  );
}

type TimelineEvent = {
  timestampSeconds: number;
  type: "alarmActive";
  point?: Point;
};

export type TimelineEvents = Record<string, TimelineEvent[]>;

export function getAlarmTimelineEvents(
  historyData: GraphicHistoryItem,
  alarms: Point[],
): TimelineEvents {
  const keys = Object.keys(historyData);
  const events: TimelineEvents = {};

  // Only get events when alarm has turned active for now
  keys.forEach((key) => {
    events[key] = [];
    // Ignore the first entry since we don't know the values
    // of the previous timestamps
    for (let i = 1; i < historyData[key].length; i++) {
      const cur = historyData[key][i];

      if (!cur.isAlarmActive) {
        continue;
      }

      // Only add a new event if this is when the alarm turned active
      // from inactive state
      if (historyData[key][i - 1].isAlarmActive) {
        continue;
      }

      events[key].push({
        timestampSeconds: cur.timestampJS.toSeconds(),
        type: "alarmActive",
        point: alarms.find(
          (a) =>
            a.device.externalId === cur.deviceId && a.name === cur.pointName,
        ),
      });
    }
  });

  return events;
}
